import { FlexBox, Skeleton, Text, Tooltip } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { PercentageBar } from 'lib-analytics-ui';
import { AgentPresenceMetric } from 'lib-dashboard-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import {
  DashboardLegend,
  DashboardLegendItem,
} from 'app/views/layout/views/dashboard/components/legend';
import {
  QueueDashboardMetric,
  type QueueDashboardMetricProps,
} from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';
import { mediaAttributes } from 'utils/channels/media';

import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

import styles from './agentPresenceMetric.module.scss';

type QueueDashboardAgentPresenceMetricProps = {
  /** The agent presence metric data. */
  metric?: AgentPresenceMetric;
} & PropsWithClassName;

export const QueueDashboardAgentPresenceMetric = memo(
  function QueueDashboardAgentPresenceMetric({
    className,
    metric,
  }: QueueDashboardAgentPresenceMetricProps): JSX.Element {
    const { queue, isSkeleton } = useQueueDashboardQueueContext();

    const channelsBreakdown = useMemo((): JSX.Element | null => {
      if (isSkeleton || !metric || !metric.online.total) return null;

      const onlineByMediaType = Object.entries(metric.online.media_type).filter(
        ([_, onlineValue]) => onlineValue,
      );

      return (
        <FlexBox flexDirection="column" gap="200">
          <Text.Paragraph variant="3" color="inherit" align="right">
            Available / Online
          </Text.Paragraph>

          {!onlineByMediaType.length ? (
            <Text.Paragraph variant="4" color="inherit">
              Online agents must have at least one channel turned on to be
              available.
            </Text.Paragraph>
          ) : (
            <DashboardLegend
              items={onlineByMediaType.map(([media, onlineValue]) => ({
                type: 'string',
                title: mediaAttributes[media].name,
                icon: mediaAttributes[media].icon,
                value: `${metric.available.media_type[media] ?? 0} / ${
                  onlineValue ?? 0
                }`,
              }))}
            />
          )}
        </FlexBox>
      );
    }, [isSkeleton, metric]);

    const availableTotal = metric?.available?.total ?? 0;
    const onlineTotal = metric?.online?.total ?? 0;

    // The number of available agents cannot be higher than the number of online agents. At best,
    // the numbers can be equal, but the count of available agents is a subset of those online.
    const percentage =
      onlineTotal && availableTotal <= onlineTotal
        ? Math.round((availableTotal / onlineTotal) * 100)
        : 0;

    const thresholdAlertTooltip =
      useMemo((): QueueDashboardMetricProps['alertTooltip'] => {
        if (!queue) return null;

        const threshold = queue.queue_thresholds?.available_agents;

        if (!metric?.threshold_breached || !threshold) {
          return null;
        }

        return {
          head: 'Available agents alert',
          content: (
            <>
              This queue is currently below the minimum required number of
              available agents, which is <b>{threshold}</b>.
            </>
          ),
        };
      }, [metric?.threshold_breached, queue]);

    const legendItems: DashboardLegendItem[] = useMemo(
      () => [
        {
          type: 'number',
          title: 'Available',
          format: 'number',
          value: availableTotal,
          color: colors.purple['700'],
        },
        {
          type: 'number',
          title: 'Online',
          format: 'number',
          value: onlineTotal,
          color: queue ? colors.neutral[200] : colors.purple[200],
        },
      ],
      [availableTotal, onlineTotal, queue],
    );

    return (
      <QueueDashboardMetric
        className={className}
        metricType="agent_presence"
        isSkeleton={isSkeleton}
        alertTooltip={thresholdAlertTooltip}
      >
        <FlexBox flexDirection="column" gap="200" maxWidth="80%">
          {isSkeleton ? (
            <Skeleton height="20px" width="auto" />
          ) : (
            <Tooltip
              className={styles.tooltip}
              content={channelsBreakdown}
              disabled={!channelsBreakdown}
            >
              <PercentageBar
                value={percentage}
                showFullBar
                theme={
                  [legendItems[0].color, legendItems[1].color] as [
                    string,
                    string,
                  ]
                }
              />
            </Tooltip>
          )}

          <DashboardLegend items={legendItems} isSkeleton={isSkeleton} />
        </FlexBox>
      </QueueDashboardMetric>
    );
  },
);
